import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http'
import {Injectable} from '@angular/core'
import {AuthService} from '@auth/services/auth.service'
import {Observable} from 'rxjs'
import {environment} from '../../environments/environment'

/**
 * Setzt die Anmeldeköpfe an JEDEN Aufruf.
 *
 * Drei Regeln, die nicht verhandelbar sind:
 *  - Immer auf `request.headers` aufbauen, nie `new HttpHeaders()`. Letzteres
 *    wirft den bestehenden Kopfzeilensatz weg.
 *  - `Authorization` nur setzen, wenn ein JWT vorliegt, sonst geht `Bearer `
 *    leer hinaus und das Backend lehnt ab, obwohl gar kein Token existiert.
 *  - Die Adresse NICHT verändern. Dieses Werkzeug baut überall vollständige
 *    Adressen (`SERVER_API_URL` + Pfad); ein Präfix erzeugte
 *    `https://…https://…`.
 */
@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const jwt = this.authService.getJWTToken()
    const now = Date.now()

    let headers = request.headers.set('x-apikey', environment.authKey)

    // Der EINZIGE Kopf, aus dem das Backend den JWT liest (`JwtValidator`).
    // Ohne ihn läuft dieses Werkzeug in eine Totalaussperrung, sobald der Host
    // auf die strenge Prüfung gehoben wird.
    if (jwt) {
      headers = headers.set('Authorization', `Bearer ${jwt}`)
    }

    // Klartext-Zeitstempel im Format, das `TimestampValidator` erwartet.
    // Wird derzeit von keiner Policy erzwungen; das Prüfprotokoll wertet ihn
    // aber aus. Mitnehmen, nicht darauf verlassen.
    headers = headers.set('x-ts', AuthInterceptorService.tsUtcString(now))

    // Altlast: Diese zwei Köpfe liest zur Anmeldung niemand mehr, nur noch das
    // Prüfprotokoll. Sie bleiben bewusst erhalten, damit die Protokollzeilen
    // unverändert lesbar sind.
    if (jwt) {
      headers = headers.set('Token', jwt)
    }
    const legacyTs = this.encodeTimestamp(now)
    if (legacyTs) {
      headers = headers.set('tsToken', legacyTs)
    }

    return next.handle(request.clone({headers}))
  }

  /** `yyyyMMddHHmmssSSS` in UTC - genau das Format des Backends. */
  private static tsUtcString(ms: number): string {
    const date = new Date(ms)
    const pad = (value: number, length = 2): string =>
      String(value).padStart(length, '0')

    return (
      String(date.getUTCFullYear()) +
      pad(date.getUTCMonth() + 1) +
      pad(date.getUTCDate()) +
      pad(date.getUTCHours()) +
      pad(date.getUTCMinutes()) +
      pad(date.getUTCSeconds()) +
      pad(date.getUTCMilliseconds(), 3)
    )
  }

  /**
   * Verschlüsselter Zeitstempel des Altpfades.
   *
   * ⚠️ Die Zeichenzuordnung wird PRO AUFRUF gelesen und war vorher ein
   * Feldwert. Die Interceptor-Instanz entsteht beim ersten HTTP-Aufruf, und das
   * ist genau die Anmeldung - da liegt das Zeichen-Token noch nicht im
   * Speicher. Als Feld gelesen blieb die Zuordnung die ganze Sitzung leer und
   * dieser Kopf damit wertlos.
   *
   * Ausserdem lag hier vorher ein `atob()` ohne Absicherung im Feldwert: Ein
   * nicht Base64-fähiges Zeichen-Token hätte den Interceptor beim Erzeugen
   * scheitern lassen und damit jeden Aufruf der Anwendung.
   */
  private encodeTimestamp(timestamp: number): string {
    let charSource: string
    try {
      charSource = atob(this.authService.getTsToken())
    } catch {
      return ''
    }
    if (!charSource || charSource.length !== 10) {
      return ''
    }

    const baseMap = '0123456789'
    const charMap: { [key: string]: string } = {}
    for (let i = 0; i < baseMap.length; i++) {
      charMap[baseMap.charAt(i)] = charSource.charAt(i)
    }

    const digits = timestamp.toString()
    let encoded = ''
    for (let i = 0; i < digits.length; i++) {
      const digit = digits.charAt(i)
      encoded += Object.prototype.hasOwnProperty.call(charMap, digit)
        ? charMap[digit]
        : digit
    }

    try {
      return btoa(encoded)
    } catch {
      return ''
    }
  }
}
